// Story 5.4 — /marketplace SPA page. The EPIC-5 READER STORY.
//
// Epic 8 fidelity pass (2026-08) against the definitive design handoff
// (design/claude-design/design_handoff_shop_market/MarketPage.dc.html):
// the Market is a LIGHT paper page (paper-100 ground, forest-900 ink).
//   * h1 "Market" — deliberately no count/subtitle under it.
//   * Filter bar: mono "Filters ▼/▲" toggle (border forest-600 when open
//     or any filter active + gold count pill), inline removable per-filter
//     pills, "Clear all" link, sort <select> pushed right.
//   * Filter panel: collapsible paper card, auto-fit checklist columns with
//     gold mono labels + 12px checkbox squares + facet counts (where the
//     backend supplies them), price min–max mono inputs, "Reset filters".
//   * States: 6 pulsing skeleton cards / "Registry unreachable" error card /
//     dashed "No listings match" empty card.
//   * Footer line + 1080px content column.
//
// The two surfaces (Opened Shares / Sealed Dossiers) stay tab-split: they
// are backed by two separate paginated endpoints and cannot be merged into
// the design's single unified list. All real behavior is preserved:
// URL-driven filters, pagination, buy-modal flow, testids. (The card's
// "Drop price €X" footer line was dropped 2026-08-21.)
//
// Facets skipped for lack of backend: condition min/max + "signed only"
// (no condition/signed axis on the browse API). Sort options are limited
// to the three the API actually supports (no "Creator A–Z" / "Best
// condition" invented sorts). Facet counts render only where the backend
// returns active_listing_count (creators, drops); tiers carry no count.
//
// URL-driven state via useSearchParams: `surface` + per-surface filter
// params + page + per_page. Deep-links work; tab switch updates URL.
//
// 401 → render sign-in prompt + Link to "/" (Story 4.2 precedent).

import {
  Button,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@greenhold/components";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { EmptyState as SharedEmptyState } from "../components/EmptyState";
import { BuyListingConfirmModal } from "../features/marketplace/BuyListingConfirmModal";
import {
  EditSealedDossierListingDialog,
  EditShareListingDialog,
} from "../features/marketplace/EditListingDialog";
import { formatCents } from "../features/marketplace/feeMath";
import {
  MarketplaceListingCard,
  type MarketplaceListingCardListing,
} from "../features/marketplace/MarketplaceListingCard";
import {
  useMarketplaceShareListings,
  type MarketplaceBrowseSort,
  type MarketplacePerPage,
  type ShareTier,
} from "../features/marketplace/useMarketplaceShareListings";
import {
  useMarketplaceSealedDossierListings,
  type MarketplaceSealedDossierListingsError,
} from "../features/marketplace/useMarketplaceSealedDossierListings";

type Surface = "shares" | "sealed";

const ALL_TIERS: ShareTier[] = ["common", "preferred", "premium", "founders"];
const PER_PAGE_OPTIONS: MarketplacePerPage[] = [24, 36, 48];
// Only the sorts the browse API actually supports — no invented
// "Creator A–Z" / "Best condition" (no such axis on the backend).
const SORT_OPTIONS: { value: MarketplaceBrowseSort; label: string }[] = [
  { value: "listed_at_desc", label: "Recently listed" },
  { value: "price_asc", label: "Price · low to high" },
  { value: "price_desc", label: "Price · high to low" },
];

// Results grid + skeleton grid share the design's auto-fill track.
const RESULTS_GRID_CLASS =
  "grid gap-6 [grid-template-columns:repeat(auto-fill,minmax(230px,1fr))]";

// The forest panel that holds the market body — the surface tabs + filters +
// artifact results — on the parchment page (the "Market" title stays on the
// paper above it). Spans the full content width. Its controls are surface-
// neutral (inherit currentColor) or light chips, so they read on the forest.
const MARKET_PANEL_CLASS =
  "rounded-xl border border-brand-gold-500/40 bg-brand-forest-900 p-5 text-brand-paper-100 shadow-md";

function parseSurface(raw: string | null): Surface {
  return raw === "sealed" ? "sealed" : "shares";
}

function parseInt1(raw: string | null, fallback: number): number {
  const n = Number(raw ?? "");
  return Number.isFinite(n) && n >= 1 ? Math.trunc(n) : fallback;
}

function parsePerPage(raw: string | null): MarketplacePerPage {
  const n = Number(raw ?? "");
  if (n === 24 || n === 36 || n === 48) return n;
  return 24;
}

function parseSort(raw: string | null): MarketplaceBrowseSort {
  if (raw === "price_asc" || raw === "price_desc") return raw;
  return "listed_at_desc";
}

function parseCsv(raw: string | null): string[] {
  if (!raw) return [];
  return raw
    .split(",")
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

function parseTiers(raw: string | null): ShareTier[] {
  return parseCsv(raw).filter((s): s is ShareTier =>
    ALL_TIERS.includes(s as ShareTier),
  );
}

function parsePriceCents(raw: string | null): number | null {
  if (!raw) return null;
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.trunc(n);
}

// Story 5.5 — Wallet balance fetch for the BuyListingConfirmModal's
// always-visible balance display (UX-DR24).
interface WalletBalanceSnapshot {
  balance_cents: number;
  escrow_cents: number;
  currency: "EUR";
}

async function fetchWalletBalanceForMarketplace(): Promise<WalletBalanceSnapshot> {
  const response = await fetch("/api/v1/wallet/balance", {
    credentials: "include",
  });
  if (!response.ok) {
    throw new Error(`balance fetch failed: ${response.status}`);
  }
  return (await response.json()) as WalletBalanceSnapshot;
}

export function MarketplacePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const surface = parseSurface(searchParams.get("surface"));
  // Story 5.5 — page-level buy-modal target. Clicking a card sets it;
  // closing the modal clears it. The modal is mounted once at page level
  // (NOT once per card) so the modal-mount + balance-query lifecycle
  // stays page-scoped.
  const [buyTarget, setBuyTarget] = useState<MarketplaceListingCardListing | null>(
    null,
  );
  // Owner editing their own listing from the buy modal's "Edit listing".
  const [editTarget, setEditTarget] = useState<MarketplaceListingCardListing | null>(
    null,
  );
  // Wallet balance for the FR59 always-visible balance display. 30s
  // staleTime mirrors useVaultItems convention.
  const balanceQuery = useQuery({
    queryKey: ["wallet-balance"],
    queryFn: fetchWalletBalanceForMarketplace,
    staleTime: 30_000,
    retry: false,
  });

  function updateParams(updates: Record<string, string | null>) {
    const next = new URLSearchParams(searchParams);
    for (const [key, value] of Object.entries(updates)) {
      if (value === null || value === "") {
        next.delete(key);
      } else {
        next.set(key, value);
      }
    }
    setSearchParams(next);
  }

  function handleListingClick(listing: MarketplaceListingCardListing) {
    setBuyTarget(listing);
  }

  return (
    // The market is a LIGHT paper page — paper-100 ground, forest-900 ink.
    // The dark forest ground is reserved for artifact display insets, not
    // market chrome. The outer div paints the ground full-bleed below the
    // app shell's dark top bar.
    <div className="min-h-[calc(100vh-3.5rem)] w-full bg-brand-paper-200 text-brand-forest-900">
      <main className="mx-auto flex w-full max-w-[1080px] flex-col gap-6 px-6 pb-16 pt-8">
        {/* h1 "Market" — deliberately no count / subtitle underneath. */}
        <h1 className="font-heading text-[1.75rem] font-bold leading-tight">
          Market
        </h1>

        <div className={MARKET_PANEL_CLASS}>
          <Tabs
            value={surface}
            onValueChange={(value) =>
              updateParams({
                surface: value === "shares" ? null : value,
                page: null,
              })
            }
          >
            <TabsList>
              <TabsTrigger value="shares">Opened Certificates</TabsTrigger>
              <TabsTrigger value="sealed">Sealed Dossiers</TabsTrigger>
            </TabsList>
            <TabsContent value="shares">
              <ShareBrowseView
                searchParams={searchParams}
                updateParams={updateParams}
                onListingClick={handleListingClick}
              />
            </TabsContent>
            <TabsContent value="sealed">
              <SealedDossierBrowseView
                searchParams={searchParams}
                updateParams={updateParams}
                onListingClick={handleListingClick}
              />
            </TabsContent>
          </Tabs>
        </div>

        {buyTarget && (
          <BuyListingConfirmModal
            listing={buyTarget}
            open={true}
            onOpenChange={(open) => {
              if (!open) setBuyTarget(null);
            }}
            balanceCents={balanceQuery.data?.balance_cents ?? null}
            onEditListing={setEditTarget}
          />
        )}

        {editTarget?.kind === "share" && (
          <EditShareListingDialog
            open
            onOpenChange={(open) => {
              if (!open) setEditTarget(null);
            }}
            listingId={editTarget.row.listing_id}
            creatorUserId={editTarget.row.share.creator_user_id}
            tier={editTarget.row.share.tier}
            signed={editTarget.row.share.signed}
            priceCents={editTarget.row.price_cents}
            allowBidding={editTarget.row.allow_bidding}
          />
        )}
        {editTarget?.kind === "sealed_dossier" && (
          <EditSealedDossierListingDialog
            open
            onOpenChange={(open) => {
              if (!open) setEditTarget(null);
            }}
            listingId={editTarget.row.listing_id}
            dropId={editTarget.row.sealed_dossier.drop_id}
            priceCents={editTarget.row.price_cents}
            allowBidding={editTarget.row.allow_bidding}
          />
        )}
      </main>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Share browse view
// ─────────────────────────────────────────────────────────────────────────

interface BrowseViewProps {
  searchParams: URLSearchParams;
  updateParams: (updates: Record<string, string | null>) => void;
  onListingClick: (listing: MarketplaceListingCardListing) => void;
}

function ShareBrowseView({
  searchParams,
  updateParams,
  onListingClick,
}: BrowseViewProps) {
  const page = parseInt1(searchParams.get("page"), 1);
  const perPage = parsePerPage(searchParams.get("per_page"));
  const creators = parseCsv(searchParams.get("creator"));
  const tiers = parseTiers(searchParams.get("tier"));
  const priceMinCents = parsePriceCents(searchParams.get("price_min"));
  const priceMaxCents = parsePriceCents(searchParams.get("price_max"));
  const sort = parseSort(searchParams.get("sort"));

  const query = useMarketplaceShareListings({
    page,
    perPage,
    creators,
    tiers,
    priceMinCents,
    priceMaxCents,
    sort,
  });

  if (query.error?.status === 401) {
    return <SignInPrompt />;
  }

  const data = query.data;

  return (
    <section className="mt-4 flex flex-col gap-4">
      <FilterBar
        kind="share"
        creators={creators}
        tiers={tiers}
        drops={[]}
        priceMinCents={priceMinCents}
        priceMaxCents={priceMaxCents}
        sort={sort}
        availableCreators={data?.filter_metadata.available_creators ?? []}
        availableDrops={[]}
        onChange={(updates) => updateParams({ ...updates, page: null })}
      />

      {query.isLoading && <LoadingSkeletons />}
      {query.isError && <ErrorNotice onRetry={() => query.refetch()} />}
      {!query.isLoading && !query.isError && data && data.data.length === 0 && (
        <EmptyState
          onReset={() =>
            updateParams({
              creator: null,
              tier: null,
              price_min: null,
              price_max: null,
              page: null,
            })
          }
        />
      )}
      {data && data.data.length > 0 && (
        <ul className={RESULTS_GRID_CLASS}>
          {data.data.map((row) => (
            <li key={row.listing_id}>
              <MarketplaceListingCard
                listing={{ kind: "share", row }}
                onClick={onListingClick}
              />
            </li>
          ))}
        </ul>
      )}

      {data && data.page_info.total_items > 0 && (
        <MarketplacePaginationFooter
          page={page}
          perPage={perPage}
          totalItems={data.page_info.total_items}
          totalPages={data.page_info.total_pages}
          onPageChange={(p) => updateParams({ page: p === 1 ? null : String(p) })}
          onPerPageChange={(pp) =>
            updateParams({ per_page: pp === 24 ? null : String(pp), page: null })
          }
        />
      )}
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Sealed-dossier browse view
// ─────────────────────────────────────────────────────────────────────────

function SealedDossierBrowseView({
  searchParams,
  updateParams,
  onListingClick,
}: BrowseViewProps) {
  const page = parseInt1(searchParams.get("page"), 1);
  const perPage = parsePerPage(searchParams.get("per_page"));
  const drops = parseCsv(searchParams.get("drop_id"));
  const priceMinCents = parsePriceCents(searchParams.get("price_min"));
  const priceMaxCents = parsePriceCents(searchParams.get("price_max"));
  const sort = parseSort(searchParams.get("sort"));

  const query = useMarketplaceSealedDossierListings({
    page,
    perPage,
    drops,
    priceMinCents,
    priceMaxCents,
    sort,
  });

  if ((query.error as MarketplaceSealedDossierListingsError | undefined)?.status === 401) {
    return <SignInPrompt />;
  }

  const data = query.data;

  return (
    <section className="mt-4 flex flex-col gap-4">
      <FilterBar
        kind="sealed"
        creators={[]}
        tiers={[]}
        drops={drops}
        priceMinCents={priceMinCents}
        priceMaxCents={priceMaxCents}
        sort={sort}
        availableCreators={[]}
        availableDrops={data?.filter_metadata.available_drops ?? []}
        onChange={(updates) => updateParams({ ...updates, page: null })}
      />

      {query.isLoading && <LoadingSkeletons />}
      {query.isError && <ErrorNotice onRetry={() => query.refetch()} />}
      {!query.isLoading && !query.isError && data && data.data.length === 0 && (
        <EmptyState
          onReset={() =>
            updateParams({
              drop_id: null,
              price_min: null,
              price_max: null,
              page: null,
            })
          }
        />
      )}
      {data && data.data.length > 0 && (
        <ul className={RESULTS_GRID_CLASS}>
          {data.data.map((row) => (
            <li key={row.listing_id}>
              <MarketplaceListingCard
                listing={{ kind: "sealed_dossier", row }}
                onClick={onListingClick}
              />
            </li>
          ))}
        </ul>
      )}

      {data && data.page_info.total_items > 0 && (
        <MarketplacePaginationFooter
          page={page}
          perPage={perPage}
          totalItems={data.page_info.total_items}
          totalPages={data.page_info.total_pages}
          onPageChange={(p) => updateParams({ page: p === 1 ? null : String(p) })}
          onPerPageChange={(pp) =>
            updateParams({ per_page: pp === 24 ? null : String(pp), page: null })
          }
        />
      )}
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────────────

function SignInPrompt() {
  return (
    <section className="space-y-3 rounded-lg border border-brand-paper-300 bg-brand-paper-50 p-6">
      <p className="font-body text-sm opacity-75">
        Sign in to browse the marketplace.
      </p>
      <Link
        to="/"
        className="text-sm text-brand-forest-600 underline hover:text-brand-forest-900"
      >
        Back to home →
      </Link>
    </section>
  );
}

// Loading — 6 pulsing skeleton cards (opacity 1→0.45, 1.4s, staggered
// 0.15s) on the design's auto-fill grid.
function LoadingSkeletons() {
  const rows = [0, 1, 2, 3, 4, 5];
  return (
    <div className={RESULTS_GRID_CLASS} aria-hidden="true">
      <style>{"@keyframes ghPulse{0%,100%{opacity:1}50%{opacity:0.45}}"}</style>
      {rows.map((k) => (
        <div
          key={k}
          className="grid justify-items-center gap-3 rounded-lg border border-brand-paper-300 bg-brand-paper-50 p-4"
        >
          <div
            className="h-[226px] w-40 rounded-md bg-brand-paper-200"
            style={{ animation: "ghPulse 1.4s ease-in-out infinite" }}
          />
          <div
            className="h-3 w-[70%] rounded bg-brand-paper-200"
            style={{ animation: "ghPulse 1.4s ease-in-out 0.15s infinite" }}
          />
          <div
            className="h-2.5 w-[45%] rounded bg-brand-paper-200"
            style={{ animation: "ghPulse 1.4s ease-in-out 0.3s infinite" }}
          />
        </div>
      ))}
    </div>
  );
}

function EmptyState({ onReset }: { onReset: () => void }) {
  return (
    <SharedEmptyState
      aria-label="No listings"
      title="No listings match"
      description="Nothing on the market fits these filters right now. Holders list when they're ready — check back after the next drop."
      action={
        // Lives on the forest panel now — force light text so the default
        // tertiary forest-green doesn't vanish into the green ground.
        <Button
          variant="tertiary"
          size="sm"
          onClick={onReset}
          className="!text-brand-paper-100/80 hover:!text-brand-paper-100"
        >
          Clear filters
        </Button>
      }
    />
  );
}

// Error — #dc4a43 (state-error-accent) bordered paper card, "Registry
// unreachable" + fixed registry-voice copy + secondary Retry.
function ErrorNotice({ onRetry }: { onRetry: () => void }) {
  return (
    <section
      aria-label="Marketplace error"
      aria-live="polite"
      className="grid justify-items-center gap-2 rounded-lg border border-state-error-accent bg-brand-paper-50 px-6 py-10 text-center"
    >
      <div className="flex items-center gap-2">
        <span
          aria-hidden="true"
          className="h-2.5 w-2.5 rounded-full bg-state-error-fill"
        />
        <b className="font-heading">Registry unreachable</b>
      </div>
      <p className="m-0 max-w-[400px] text-[0.85rem] opacity-70">
        The market ledger didn&apos;t answer. Your holdings are safe — asks and
        floors return the moment the registry responds.
      </p>
      <div className="mt-2">
        <Button variant="secondary" size="sm" onClick={onRetry}>
          Retry
        </Button>
      </div>
    </section>
  );
}

interface FilterFacetOption {
  key: string;
  label: string;
  capitalize?: boolean;
  count: number | null;
  selected: boolean;
  toggle: () => void;
}

interface FilterPill {
  key: string;
  label: string;
  ariaLabel: string;
  remove: () => void;
}

interface FilterBarProps {
  kind: "share" | "sealed";
  creators: string[];
  tiers: ShareTier[];
  drops: string[];
  priceMinCents: number | null;
  priceMaxCents: number | null;
  sort: MarketplaceBrowseSort;
  availableCreators: {
    id: string;
    display_name: string;
    active_listing_count: number;
  }[];
  availableDrops: {
    drop_id: string;
    portfolio_title: string;
    active_listing_count: number;
  }[];
  onChange: (updates: Record<string, string | null>) => void;
}

// Gold mono uppercase filter-column heading (0.6rem, letter-spacing 0.1em).
const FILTER_LABEL_CLASS =
  "font-mono text-[0.6rem] uppercase tracking-[0.1em] text-brand-gold-600";

// Mono euro price input (70px) — paper-100 well, paper-300 hairline.
const PRICE_INPUT_CLASS =
  "w-[70px] rounded border border-brand-paper-300 bg-brand-paper-100 px-2 py-[5px] font-mono text-[0.72rem] text-brand-forest-900 focus:outline-none focus:ring-2 focus:ring-brand-forest-500";

function eurosFromCents(cents: number | null): string {
  if (cents == null) return "";
  return String(cents / 100);
}

function centsFromEuros(raw: string): string | null {
  if (raw.trim() === "") return null;
  const euros = Number(raw);
  if (!Number.isFinite(euros) || euros < 0) return null;
  return String(Math.round(euros * 100));
}

function FilterFacetColumn({
  label,
  options,
}: {
  label: string;
  options: FilterFacetOption[];
}) {
  if (options.length === 0) return null;
  return (
    <div className="grid content-start gap-2">
      <div className={FILTER_LABEL_CLASS}>{label}</div>
      <div className="grid max-h-[230px] gap-0.5 overflow-x-hidden overflow-y-auto">
        {options.map((o) => (
          <button
            key={o.key}
            type="button"
            aria-pressed={o.selected}
            onClick={o.toggle}
            className={`flex w-full items-center gap-[9px] rounded px-2 py-1.5 text-left text-[0.78rem] text-brand-forest-900 hover:bg-brand-paper-200 ${
              o.selected ? "bg-brand-forest-50" : "bg-transparent"
            }`}
          >
            <span
              aria-hidden="true"
              className={`h-3 w-3 shrink-0 rounded-sm border ${
                o.selected
                  ? "border-brand-forest-600 bg-brand-forest-600"
                  : "border-[#cbc5b4] bg-brand-paper-50"
              }`}
            />
            <span className={`flex-1 ${o.capitalize ? "capitalize" : ""}`}>
              {o.label}
            </span>
            {o.count != null && (
              <span className="font-mono text-[0.62rem] opacity-50">
                {o.count}
              </span>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}

function FilterBar({
  kind,
  creators,
  tiers,
  drops,
  priceMinCents,
  priceMaxCents,
  sort,
  availableCreators,
  availableDrops,
  onChange,
}: FilterBarProps) {
  // Collapsible filter panel per the design (mono "Filters" toggle with an
  // active-count pill). Presentation-only local state — filter VALUES stay
  // URL-driven exactly as before.
  const [open, setOpen] = useState(false);

  const activeCount =
    (kind === "share" ? creators.length + tiers.length : drops.length) +
    (priceMinCents != null ? 1 : 0) +
    (priceMaxCents != null ? 1 : 0);
  const hasActive = activeCount > 0;

  function toggleCsvValue(
    param: "tier" | "creator" | "drop_id",
    current: string[],
    value: string,
  ) {
    const set = new Set(current);
    if (set.has(value)) set.delete(value);
    else set.add(value);
    onChange({ [param]: set.size === 0 ? null : Array.from(set).join(",") });
  }

  function clearAll() {
    if (kind === "share") {
      onChange({ creator: null, tier: null, price_min: null, price_max: null });
    } else {
      onChange({ drop_id: null, price_min: null, price_max: null });
    }
  }

  // ── Facet checklist options (counts only where the backend supplies them) ──
  const tierOptions: FilterFacetOption[] = ALL_TIERS.map((t) => ({
    key: `tier-${t}`,
    label: t,
    capitalize: true,
    count: null, // no per-tier count on the browse API
    selected: tiers.includes(t),
    toggle: () => toggleCsvValue("tier", tiers, t),
  }));
  const creatorOptions: FilterFacetOption[] = availableCreators.map((c) => ({
    key: `creator-${c.id}`,
    label: c.display_name,
    count: c.active_listing_count,
    selected: creators.includes(c.id),
    toggle: () => toggleCsvValue("creator", creators, c.id),
  }));
  const dropOptions: FilterFacetOption[] = availableDrops.map((d) => ({
    key: `drop-${d.drop_id}`,
    label: d.portfolio_title,
    count: d.active_listing_count,
    selected: drops.includes(d.drop_id),
    toggle: () => toggleCsvValue("drop_id", drops, d.drop_id),
  }));

  // ── Active-filter pills (inline in the bar row) ──
  const pills: FilterPill[] = [];
  if (kind === "share") {
    for (const id of creators) {
      const meta = availableCreators.find((c) => c.id === id);
      const label = meta?.display_name ?? "Creator";
      pills.push({
        key: `pill-creator-${id}`,
        label,
        ariaLabel: `Remove filter Creator: ${label}`,
        remove: () => toggleCsvValue("creator", creators, id),
      });
    }
    for (const t of tiers) {
      pills.push({
        key: `pill-tier-${t}`,
        label: t,
        ariaLabel: `Remove filter Tier: ${t}`,
        remove: () => toggleCsvValue("tier", tiers, t),
      });
    }
  } else {
    for (const id of drops) {
      const meta = availableDrops.find((d) => d.drop_id === id);
      const label = meta?.portfolio_title ?? "Drop";
      pills.push({
        key: `pill-drop-${id}`,
        label,
        ariaLabel: `Remove filter Drop: ${label}`,
        remove: () => toggleCsvValue("drop_id", drops, id),
      });
    }
  }
  if (priceMinCents != null) {
    pills.push({
      key: "pill-price-min",
      label: `Min ${formatCents(priceMinCents)}`,
      ariaLabel: `Remove filter Min ${formatCents(priceMinCents)}`,
      remove: () => onChange({ price_min: null }),
    });
  }
  if (priceMaxCents != null) {
    pills.push({
      key: "pill-price-max",
      label: `Max ${formatCents(priceMaxCents)}`,
      ariaLabel: `Remove filter Max ${formatCents(priceMaxCents)}`,
      remove: () => onChange({ price_max: null }),
    });
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Control row: Filters toggle + inline pills + Clear all · sort right */}
      <div
        className="flex flex-wrap items-center gap-2"
        data-testid="active-filters-row"
      >
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          className={`flex items-center gap-2 rounded border bg-brand-paper-50 px-[13px] py-[7px] font-mono text-[0.68rem] uppercase tracking-[0.08em] text-brand-forest-900 ${
            open || hasActive
              ? "border-brand-forest-600"
              : "border-brand-paper-300"
          }`}
        >
          Filters
          {hasActive && (
            <span className="rounded-full bg-brand-gold-500 px-[7px] py-px text-[0.6rem] font-semibold text-brand-ink-900">
              {activeCount}
            </span>
          )}
          <span aria-hidden="true" className="text-[0.6rem] opacity-55">
            {open ? "▲" : "▼"}
          </span>
        </button>

        {pills.map((pill) => (
          <button
            key={pill.key}
            type="button"
            onClick={pill.remove}
            aria-label={pill.ariaLabel}
            title="Remove filter"
            className="flex items-center gap-[7px] rounded-full border border-brand-forest-100 bg-brand-forest-50 px-[11px] py-1.5 text-[0.72rem] text-brand-forest-900 hover:border-brand-forest-600"
          >
            <span className="capitalize">{pill.label}</span>
            <span aria-hidden="true" className="text-[0.8rem] leading-none opacity-50">
              ×
            </span>
          </button>
        ))}

        {hasActive && (
          <button
            type="button"
            onClick={clearAll}
            className="px-1 text-[0.72rem] text-brand-paper-100/70 underline hover:text-brand-paper-100"
          >
            Clear all
          </button>
        )}

        <select
          aria-label="Sort listings"
          value={sort}
          onChange={(e) =>
            onChange({
              sort: e.target.value === "listed_at_desc" ? null : e.target.value,
            })
          }
          className="ml-auto rounded border border-brand-paper-300 bg-brand-paper-50 px-[10px] py-[7px] font-mono text-[0.72rem] text-brand-forest-900 focus:outline-none focus:ring-2 focus:ring-brand-forest-500"
        >
          {SORT_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      {/* Collapsible filter panel — paper card, auto-fit checklist columns */}
      {open && (
        <div className="grid gap-6 rounded-lg border border-brand-paper-300 bg-brand-paper-50 p-5 [grid-template-columns:repeat(auto-fit,minmax(190px,1fr))]">
          {kind === "share" && (
            <FilterFacetColumn label="Tier" options={tierOptions} />
          )}
          {kind === "share" && (
            <FilterFacetColumn label="Creator" options={creatorOptions} />
          )}
          {kind === "sealed" && (
            <FilterFacetColumn label="Dossier" options={dropOptions} />
          )}

          {/* Price min–max mono inputs (euros) + Reset filters */}
          <div className="grid content-start gap-3.5">
            <div className="grid gap-2">
              <div className={FILTER_LABEL_CLASS}>Price · €</div>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min={0}
                  inputMode="decimal"
                  placeholder="min"
                  aria-label="Minimum price in euros"
                  value={eurosFromCents(priceMinCents)}
                  onChange={(e) =>
                    onChange({ price_min: centsFromEuros(e.target.value) })
                  }
                  className={PRICE_INPUT_CLASS}
                />
                <span className="text-[0.75rem] opacity-45">–</span>
                <input
                  type="number"
                  min={0}
                  inputMode="decimal"
                  placeholder="max"
                  aria-label="Maximum price in euros"
                  value={eurosFromCents(priceMaxCents)}
                  onChange={(e) =>
                    onChange({ price_max: centsFromEuros(e.target.value) })
                  }
                  className={PRICE_INPUT_CLASS}
                />
              </div>
            </div>
            <div>
              <Button
                variant="tertiary"
                size="sm"
                disabled={!hasActive}
                onClick={clearAll}
              >
                Reset filters
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

interface MarketplacePaginationFooterProps {
  page: number;
  perPage: MarketplacePerPage;
  totalItems: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onPerPageChange: (perPage: MarketplacePerPage) => void;
}

function MarketplacePaginationFooter({
  page,
  perPage,
  totalPages,
  onPageChange,
  onPerPageChange,
}: MarketplacePaginationFooterProps) {
  // 7 page numbers max, centered on current page.
  const pageNumbers: (number | "ellipsis")[] = [];
  const radius = 3;
  for (let i = 1; i <= totalPages; i++) {
    if (i === 1 || i === totalPages || (i >= page - radius && i <= page + radius)) {
      pageNumbers.push(i);
    } else if (pageNumbers[pageNumbers.length - 1] !== "ellipsis") {
      pageNumbers.push("ellipsis");
    }
  }

  return (
    <nav
      aria-label="Marketplace pagination"
      className="flex flex-wrap items-center justify-between gap-3 pt-2"
    >
      <div className="flex flex-wrap items-center gap-2">
        <Button
          variant="secondary"
          disabled={page <= 1}
          onClick={() => onPageChange(Math.max(1, page - 1))}
        >
          ← Previous
        </Button>
        <div className="flex items-center gap-1">
          {pageNumbers.map((p, idx) =>
            p === "ellipsis" ? (
              <span key={`ellipsis-${idx}`} className="px-1 text-sm opacity-60">
                …
              </span>
            ) : (
              <button
                key={p}
                type="button"
                aria-current={p === page ? "page" : undefined}
                onClick={() => onPageChange(p)}
                className={`rounded-md px-3 py-1 text-sm ${p === page ? "bg-brand-forest-500 text-brand-paper-100" : "hover:bg-brand-paper-200"}`}
              >
                {p}
              </button>
            ),
          )}
        </div>
        <Button
          variant="secondary"
          disabled={page >= totalPages}
          onClick={() => onPageChange(page + 1)}
        >
          Next →
        </Button>
      </div>
      <label className="flex items-center gap-2 text-xs opacity-80">
        Items per page
        <select
          value={perPage}
          onChange={(e) =>
            onPerPageChange(Number(e.target.value) as MarketplacePerPage)
          }
          className="rounded border border-brand-paper-300 bg-brand-paper-50 px-2 py-1 font-mono text-xs text-brand-forest-900 focus:outline-none focus:ring-2 focus:ring-brand-forest-500"
        >
          {PER_PAGE_OPTIONS.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      </label>
    </nav>
  );
}
